import type { LLMStreamEvent } from '../../../contracts/harness.types';

export type WorkTurnMetricsSnapshot = {
  duration_ms?: number;
  time_to_first_token_ms?: number;
  response_length?: number;
  tool_call_count?: number;
  tools_used?: string[];
  had_approval_request?: boolean;
  is_agentic_use?: boolean;
};

// Keep this privacy allowlist aligned with ml-scribe's work_job_completed
// _JOB_TYPE_BY_TOOL keys. Unknown/free-text tool names never reach analytics.
export const WORK_ANALYTICS_TOOL_NAMES = [
  'generate_prechart_document',
  'get_structured_prechart',
  'save_structured_prechart',
  'fill_pdf_form',
  'fill_word_form',
  'fill_excel_template',
  'create_letter',
  'update_letter',
  'discover_note_push_fields',
  'get_note_push_hints',
  'record_note_push_hints',
  'ehr.sync_session_note',
  'integrations.ehr.push_note',
  'integrations.prior_auth.inquire',
] as const;
const WORK_ANALYTICS_TOOL_NAME_SET = new Set<string>(WORK_ANALYTICS_TOOL_NAMES);

/**
 * Accumulates turn-level timing and stream counters for work_run_completed.
 * Only records values that can be measured from timestamps / stream events.
 */
export class WorkTurnMetricsTracker {
  private startedAtMs: number | null = null;
  private firstTokenAtMs: number | null = null;
  private responseLength = 0;
  private toolCallCount = 0;
  private toolNames = new Set<string>();
  private hadApprovalRequest = false;

  /** Call when the turn is submitted (or turn_started arrives). */
  markStarted(atMs: number = Date.now()): void {
    this.startedAtMs = atMs;
    this.firstTokenAtMs = null;
    this.responseLength = 0;
    this.toolCallCount = 0;
    this.toolNames.clear();
    this.hadApprovalRequest = false;
  }

  observe(event: LLMStreamEvent, atMs: number = Date.now()): void {
    if (this.startedAtMs == null) {
      if (event.type === 'turn_started') {
        this.markStarted(atMs);
      }
      return;
    }

    if (
      this.firstTokenAtMs == null &&
      event.type === 'text_delta' &&
      typeof event.text === 'string' &&
      event.text.length > 0
    ) {
      this.firstTokenAtMs = atMs;
    }

    if (event.type === 'text_delta' && typeof event.text === 'string') {
      this.responseLength += event.text.length;
    }

    if (event.type === 'tool_call_start') {
      this.toolCallCount += 1;
      if (event.tool_name && WORK_ANALYTICS_TOOL_NAME_SET.has(event.tool_name)) {
        this.toolNames.add(event.tool_name);
      }
    }

    if (event.type === 'approval_requested') {
      this.hadApprovalRequest = true;
    }
  }

  snapshot(atMs: number = Date.now()): WorkTurnMetricsSnapshot {
    if (this.startedAtMs == null) return {};

    const duration_ms = Math.max(0, atMs - this.startedAtMs);
    const time_to_first_token_ms =
      this.firstTokenAtMs != null
        ? Math.max(0, this.firstTokenAtMs - this.startedAtMs)
        : undefined;

    return {
      duration_ms,
      ...(time_to_first_token_ms != null ? { time_to_first_token_ms } : {}),
      ...(this.responseLength > 0 ? { response_length: this.responseLength } : {}),
      ...(this.toolCallCount > 0 ? { tool_call_count: this.toolCallCount } : {}),
      tools_used: [...this.toolNames].sort(),
      had_approval_request: this.hadApprovalRequest,
      is_agentic_use: this.toolCallCount > 0,
    };
  }

  reset(): void {
    this.startedAtMs = null;
    this.firstTokenAtMs = null;
    this.responseLength = 0;
    this.toolCallCount = 0;
    this.toolNames.clear();
    this.hadApprovalRequest = false;
  }
}
